import { getCartCookie } from "@/utils/cart/getCartCookie";
import { useMarket } from "@app/_context";
import type {
  Cart,
  SetDeliveryOptionMutation,
  SetDeliveryOptionMutationVariables,
} from "@server/gql/graphql";
import { setDeliveryOption } from "@server/requests/delivery/setDeliveryOption";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

export type DeliveryOptionInput = Omit<
  SetDeliveryOptionMutationVariables,
  "locale" | "cartId"
>;

type UpdateDeliveryOption = <T extends boolean = false>(
  deliveryOption: DeliveryOptionInput,
  async?: T,
) => T extends true
  ? Promise<SetDeliveryOptionMutation["setDeliveryOption"] | undefined>
  : void;

export default function useUpdateDeliveryOption() {
  const { serviceLocale: locale } = useMarket().state.market;
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isError, isPending, isSuccess } = useMutation({
    mutationFn: async (deliveryOption: DeliveryOptionInput) => {
      const cartId = getCartCookie()?.value;
      if (!cartId) return Promise.resolve(undefined);
      return setDeliveryOption({
        ...deliveryOption,
        locale,
        cartId,
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["cart"] });
      await queryClient.cancelQueries({ queryKey: ["checkout"] });
      await queryClient.cancelQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.cancelQueries({ queryKey: ["payment"] });

      const previousCart = queryClient.getQueryData<Cart>(["cart"]);
      return { previousCart };
    },
    onError: (
      error,
      input,
      context: { previousCart: Cart | undefined } | undefined,
    ) => {
      if (context?.previousCart) {
        queryClient.setQueryData<Cart>(["cart"], context.previousCart);
      }
    },
    onSettled: async () => {
      void queryClient.invalidateQueries({ queryKey: ["cart"] });
      void queryClient.invalidateQueries({ queryKey: ["checkout"] });
      void queryClient.invalidateQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.invalidateQueries({ queryKey: ["payment"] });
    },
  });

  const updateDeliveryOption = useCallback(
    (deliveryOption: DeliveryOptionInput, async = false) => {
      if (async) return mutateAsync(deliveryOption);
      mutate(deliveryOption);
    },
    [mutate, mutateAsync],
  );

  return {
    updateDeliveryOption: updateDeliveryOption as UpdateDeliveryOption,
    isErrorUpdateDeliveryOption: isError,
    isPendingUpdateDeliveryOption: isPending,
    isSuccessUpdateDeliveryOption: isSuccess,
  };
}
